import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState, useEffect } from 'react';
import { ThumbnailOverlay as ThumbnailOverlayType } from '@/store/videoFeatures';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import './thumbnail-animations.css';

interface ThumbnailOverlayProps {
  thumbnail: ThumbnailOverlayType;
  type: 'initial' | 'pause' | 'end';
  onButtonClick?: () => void;
  onThumbnailClick?: () => void;
}

export const ThumbnailOverlay: React.FC<ThumbnailOverlayProps> = ({
  thumbnail,
  type,
  onButtonClick,
  onThumbnailClick
}) => {
  useLanguage();
  const [showButton, setShowButton] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Only show button for pause thumbnails and when it's configured
    if (type === 'pause' && thumbnail?.actionButton) {
      // Add a small delay for the animation effect
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowButton(false);
    }
  }, [thumbnail, type]);

  // Add thorough null checks - AFTER hooks
  if (!thumbnail || !thumbnail.enabled || !thumbnail.imageUrl) return null;

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onButtonClick) onButtonClick();

    // Handle both formats of actionButton with null checks
    const actionButton = thumbnail.actionButton as any;
    let linkUrl = '';
    
    if (actionButton) {
      // Check for url (modern format)
      if (actionButton.url) {
        linkUrl = actionButton.url;
      }
      // Check for link (legacy format)
      else if (actionButton.link) {
        linkUrl = actionButton.link;
      }
    }
    
    if (linkUrl) {
      window.open(linkUrl, '_blank');
    }
  };

  // Position classes for button - todas as novas posições
  const positionClasses: Record<string, string> = {
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    'left': 'top-1/2 left-4 -translate-y-1/2',
    'right': 'top-1/2 right-4 -translate-y-1/2',
    'center-left': 'top-1/2 left-4 -translate-y-1/2',
    'center-right': 'top-1/2 right-4 -translate-y-1/2',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  // Size classes for button
  const sizeClasses = {
    'small': 'text-sm px-3 py-1',
    'medium': 'text-base px-4 py-2',
    'large': 'text-lg px-6 py-3',
  };

  // Handle different formats of actionButton properties with robust null checks
  const getButtonSize = () => {
    if (!thumbnail.actionButton) return 'medium';
    
    const actionButton = thumbnail.actionButton as any;
    
    // Check modern style first
    if (actionButton.style && actionButton.style.size) {
      return actionButton.style.size;
    }
    // Check direct size property
    if (actionButton.size) {
      return actionButton.size;
    }
    
    return 'medium';
  };

  const getButtonPosition = () => {
    if (!thumbnail.actionButton) return 'bottom-center';

    const actionButton = thumbnail.actionButton as any;
    return actionButton.position || 'bottom-center';
  };

  const getButtonBackgroundColor = () => {
    if (!thumbnail.actionButton) return '#3b82f6';
    
    const actionButton = thumbnail.actionButton as any;
    
    // Check modern style first
    if (actionButton.style && actionButton.style.backgroundColor) {
      return actionButton.style.backgroundColor;
    }
    // Check colors object
    if (actionButton.colors && actionButton.colors.background) {
      return actionButton.colors.background;
    }
    // Check direct property
    if (actionButton.backgroundColor) {
      return actionButton.backgroundColor;
    }
    
    return '#3b82f6';
  };

  const getButtonTextColor = () => {
    if (!thumbnail.actionButton) return '#ffffff';
    
    const actionButton = thumbnail.actionButton as any;
    
    // Check modern style first
    if (actionButton.style && actionButton.style.textColor) {
      return actionButton.style.textColor;
    }
    // Check colors object
    if (actionButton.colors && actionButton.colors.text) {
      return actionButton.colors.text;
    }
    // Check direct property
    if (actionButton.textColor) {
      return actionButton.textColor;
    }
    
    return '#ffffff';
  };

  // Get button text with fallback
  const getButtonText = () => {
    if (!thumbnail.actionButton) return translateInterfaceText("Continue Assistindo");
    
    const actionButton = thumbnail.actionButton as any;
    return actionButton.text || translateInterfaceText("Continue Assistindo");
  };

  // Get hover colors
  const getButtonHoverColor = () => {
    if (!thumbnail.actionButton) return '#2563eb';
    
    const actionButton = thumbnail.actionButton as any;
    return actionButton.colors?.hover || '#2563eb';
  };

  const getButtonHoverTextColor = () => {
    if (!thumbnail.actionButton) return '#ffffff';
    
    const actionButton = thumbnail.actionButton as any;
    return actionButton.colors?.hoverText || '#ffffff';
  };

  // Check if pulse animation is enabled
  const shouldPulse = () => {
    if (!thumbnail.actionButton) return false;
    
    const actionButton = thumbnail.actionButton as any;
    return actionButton.animations?.pulse || false;
  };

  return (
    <div
      className="absolute inset-0 bg-black flex items-center justify-center cursor-pointer z-20"
      onClick={onThumbnailClick}
      data-thumbnail-type={type}
    >
      {/* Thumbnail image */}
      <img
        src={thumbnail.imageUrl}
        alt={translateInterfaceText("{value0} thumbnail", { value0: (type) })}
        className="w-full h-full object-contain"
      />

      {/* Action button if configured - only for pause thumbnails */}
      {type === 'pause' && thumbnail.actionButton && (
        <Button
          ref={buttonRef}
          className={cn(
            "absolute z-10 thumbnail-button",
            positionClasses[getButtonPosition()] || positionClasses['center'],
            sizeClasses[getButtonSize()],
            showButton ? "opacity-100" : "opacity-0"
          )}
          style={{
            backgroundColor: getButtonBackgroundColor(),
            color: getButtonTextColor(),
            transition: 'none !important',
            animation: 'none !important',
            transform: positionClasses[getButtonPosition()]?.includes('translate')
              ? positionClasses[getButtonPosition()].match(/(-?translate-[xy]-\S+)/g)?.map(c => {
                  if (c === '-translate-x-1/2') return 'translateX(-50%)';
                  if (c === '-translate-y-1/2') return 'translateY(-50%)';
                  return '';
                }).join(' ')
              : 'none',
            willChange: 'auto',
          } as React.CSSProperties}
          onMouseEnter={(e) => {
            const currentTransform = window.getComputedStyle(e.currentTarget).transform;
            e.currentTarget.style.backgroundColor = getButtonHoverColor();
            e.currentTarget.style.color = getButtonHoverTextColor();
            e.currentTarget.style.transform = currentTransform;
          }}
          onMouseLeave={(e) => {
            const currentTransform = window.getComputedStyle(e.currentTarget).transform;
            e.currentTarget.style.backgroundColor = getButtonBackgroundColor();
            e.currentTarget.style.color = getButtonTextColor();
            e.currentTarget.style.transform = currentTransform;
          }}
          onClick={handleButtonClick}
        >
          {getButtonText()}
        </Button>
      )}
    </div>
  );
};
